import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';
import { Button } from './Button';

interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  hasUnsavedChanges?: boolean;
}

const SIZE: Record<NonNullable<SlideOverProps['size']>, string> = {
  md: 'max-w-full sm:max-w-md',
  lg: 'max-w-full md:max-w-2xl',
  xl: 'max-w-full md:max-w-4xl',
};

const OVERLAY_SHADOW = 'shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]';

export const SlideOver: React.FC<SlideOverProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  hasUnsavedChanges = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isUnsavedConfirmOpen, setIsUnsavedConfirmOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 200);
      document.body.style.overflow = 'unset';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setIsUnsavedConfirmOpen(true);
      return;
    }
    onClose();
  };

  if (!isVisible) return null;

  const slideOverContent = (
    <div className="fixed inset-0 z-[70] overflow-hidden">
      <div
        className={`absolute inset-0 bg-slate-900/40 transition-opacity duration-200 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={requestClose}
      />

      <div className="absolute inset-y-0 right-0 flex max-w-full pointer-events-none">
        <div
          className={`pointer-events-auto relative w-screen ${SIZE[size]} transform transition-transform duration-200 ease-out ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className={`flex h-full flex-col bg-white border-l border-slate-200 ${OVERLAY_SHADOW}`}>
            <div className="flex items-center justify-between px-4 h-14 border-b border-slate-200 flex-shrink-0">
              <h2 className="text-[15px] font-semibold text-slate-900 tracking-tight pr-2 truncate">
                {title}
              </h2>
              <button
                type="button"
                onClick={requestClose}
                className="text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-md p-1 flex-shrink-0"
                aria-label="Schließen"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>

            {hasUnsavedChanges && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex-shrink-0">
                <div className="flex items-center gap-2 text-amber-800 text-[12px] font-medium">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
                  <span>Ungespeicherte Änderungen</span>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 text-[13px] text-slate-700">{children}</div>

            {footer && (
              <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 flex-shrink-0">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={isUnsavedConfirmOpen}
        onClose={() => setIsUnsavedConfirmOpen(false)}
        onConfirm={() => {
          setIsUnsavedConfirmOpen(false);
          onClose();
        }}
        title="Änderungen verwerfen"
        message="Sie haben ungespeicherte Änderungen. Möchten Sie wirklich abbrechen?"
        confirmText="Verwerfen"
        cancelText="Weiter bearbeiten"
        variant="danger"
      />
    </div>
  );

  return createPortal(slideOverContent, document.body);
};

export default SlideOver;

interface SlideOverFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  loading?: boolean;
}

export const SlideOverFooter: React.FC<SlideOverFooterProps> = ({
  onCancel,
  onConfirm,
  confirmText = 'Speichern',
  cancelText = 'Abbrechen',
  confirmDisabled = false,
  loading = false,
}) => {
  return (
    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
      <Button variant="secondary" onClick={onCancel} disabled={loading} className="w-full sm:w-auto">
        {cancelText}
      </Button>
      <Button onClick={onConfirm} disabled={confirmDisabled || loading} className="w-full sm:w-auto">
        {loading ? 'Wird gespeichert…' : confirmText}
      </Button>
    </div>
  );
};
